/**
 * `/invite?code=ABCD1234[&mode=here]` — the link a child shares so a parent
 * can link with one tap (TUP-1835). PUBLIC: it sits outside the _protected layout,
 * which would bounce a signed-out parent to /start and lose the invitation.
 * Every state renders in InviteShell (top bar, hero, title, subtitle,
 * callout, pinned actions), from the approved reference screens.
 *
 * Flow
 *   signed out   "Link to …" + Create account / I already have an account.
 *                The code is parked in localStorage (lib/pending-invite.ts)
 *                so it survives signup, sign-in and phone OTP; those forms
 *                return here afterwards.
 *   signed in    the signed-in account card (Switch if it is not you) and
 *                Continue.
 *   Continue     one of: consent | already linked | blocked by the plan
 *                (full / syllabus not covered) | child linked elsewhere |
 *                expired | invalid or used.
 *   consent      the shared consent step, then POST /pairing/redeem, which
 *                consents and links in one step. No plan comes first; after
 *                linking the parent lands on My children, which offers plans
 *                to a child no plan covers yet.
 *
 * `mode=here` means the student opened the link on their own phone. After
 * linking the parent is signed out so the student gets the phone back.
 *
 * code-info needs a session, so the child's first name is only known once the
 * parent is signed in; before that the copy says "your child".
 */

import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconLinkOff,
} from '@tabler/icons-react';
import { AppShell } from '@tupai/shell';
import { useAuthStore } from '@/store/auth-store';
import { useLinksStore } from '@/store/links-store';
import ChildConsent from '@/components/child-consent';
import InviteShell from '@/components/invite-shell';
import { getAccess, getPairingCodeInfo } from '@/lib/api';
import { identityHue } from '@/lib/child-identity';
import {
  clearsInvite,
  exitFlow,
  maskPhone,
  type ExitOutcome,
  viewFromCodeInfo,
  viewFromError,
  type InviteView,
} from '@/lib/invite-flow';
import {
  clearPendingInvite,
  getPendingInvite,
  normaliseInviteCode,
  setPendingInvite,
  type PendingInvite,
} from '@/lib/pending-invite';
import { gateCopy } from '@/lib/gate-copy';
import { isSubscribed, seatTier } from '@/lib/plans';
import { useTranslation } from '@tupai/i18n/react';

export const Route = createFileRoute('/invite')({
  component: InvitePage,
});

type Phase = 'view' | 'later' | 'handback';
type Load = { status: 'loading' } | { status: 'done'; view: InviteView };

/** The URL's code wins and is parked for the sign-in detour; with no code in
 *  the URL (back from sign-in), the parked one is used. A `?code=` that is
 *  malformed is an invalid invitation, never a fallback to an older one. */
function resolveInvite(search: URLSearchParams): PendingInvite | null {
  const raw = search.get('code');
  if (raw !== null) {
    const code = normaliseInviteCode(raw);
    if (!code) return null;
    const invite: PendingInvite = {
      code,
      ...(search.get('mode') === 'here' ? { mode: 'here' } : {}),
    };
    setPendingInvite(invite);
    return invite;
  }
  return getPendingInvite();
}

const named = (name: string | null) => ({ hasName: String(name !== null), childName: name ?? '' });

function InvitePage() {
  const navigate = useNavigate();
  // Raw query string: the default JSON search parser turns codes like 23456E78 into numbers.
  const searchStr = useLocation({ select: (l) => l.searchStr });
  const { t } = useTranslation('invite');
  const { t: ta } = useTranslation('account');
  const { t: tc } = useTranslation('children');
  const { token, parentName, phone, email, logout } = useAuthStore();

  // Resolved once per mount; later renders must not re-read the URL or the
  // store (a "Not now" keeps the stored invite, a success clears it).
  const [invite] = useState(() => resolveInvite(new URLSearchParams(searchStr)));

  const [phase, setPhase] = useState<Phase>('view');
  // The parent has confirmed the signed-in account with Continue.
  const [confirmed, setConfirmed] = useState(false);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [reloadTick, setReloadTick] = useState(0);
  // A screen decided by the redeem call (blocked, linked elsewhere, expired…)
  // overrides what code-info said.
  const [override, setOverride] = useState<InviteView | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [linkedName, setLinkedName] = useState<string | null>(null);
  const [handbackVariant, setHandbackVariant] = useState<ExitOutcome>('linked');
  // The household's plan, read only for the "plan is full" copy.
  const [plan, setPlan] = useState<{ name: string; seats: number } | null>(null);
  // Whether no paid plan covers the household; null until it is read.
  const [free, setFree] = useState<boolean | null>(null);

  const code = invite?.code ?? null;
  const signedIn = token !== null;
  const accountName = parentName || email || phone;

  // Ask the server about this code as soon as there is a session, so the
  // title can name the child and the answer is ready for Continue.
  useEffect(() => {
    if (!signedIn || !code || phase === 'handback') return;
    let cancelled = false;
    setLoad({ status: 'loading' });
    getPairingCodeInfo(code)
      .then(viewFromCodeInfo, (err) => viewFromError(err))
      .then((view) => {
        if (!cancelled) setLoad({ status: 'done', view });
      });
    return () => {
      cancelled = true;
    };
    // phase is read only to skip the fetch after a hand-back sign-out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, code, reloadTick]);

  const view: InviteView | null = override ?? (load.status === 'done' ? load.view : null);
  // Expired and invalid do not depend on who is signed in, so they show at
  // once; everything else waits for the parent to confirm the account.
  const accountFree = view?.kind === 'expired' || view?.kind === 'invalid';
  const showView = signedIn && view !== null && (confirmed || accountFree);
  const childName = view && 'childName' in view ? view.childName : null;

  // A spent invitation is dropped when its screen is shown, not before: the
  // parent may still switch to the account that can use it.
  useEffect(() => {
    if (phase === 'view' && showView && view && clearsInvite(view)) clearPendingInvite();
  }, [phase, showView, view]);

  // "Your plan is full" names the plan and its places when they can be read.
  const fullPlan = showView && view?.kind === 'blocked' && view.reason === 'seat_limit_reached';
  useEffect(() => {
    if (!fullPlan) return;
    let cancelled = false;
    getAccess()
      .then((access) => {
        if (cancelled) return;
        const paid = isSubscribed(access);
        setFree(!paid);
        if (paid && access.seat_tier) {
          const tier = seatTier(access.seat_tier);
          setPlan({ name: tier.name, seats: tier.maxStudents });
        }
      })
      .catch(() => {
        if (!cancelled) setFree(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fullPlan]);

  const handleConsent = async (typedName?: string) => {
    if (!code || redeeming) return;
    setRedeeming(true);
    setRedeemError('');
    try {
      // The store's redeem also refreshes the parent's links.
      await useLinksStore.getState().handleAddChildSubmit(code, typedName);
      clearPendingInvite();
      // My children lists the child and, when no plan covers the child yet,
      // the offer to choose one; on the student's phone it is the hand-back.
      leave('linked', typedName);
    } catch (err) {
      const next = viewFromError(err, childName);
      if (next.kind === 'error') setRedeemError(t('errorBody'));
      else setOverride(next);
    } finally {
      setRedeeming(false);
    }
  };

  const switchAccount = () => {
    // The invitation stays stored: the next sign-in returns here.
    logout();
    setConfirmed(false);
    setOverride(null);
    navigate({ to: '/invite', replace: true });
  };

  const retry = () => {
    setOverride(null);
    setRedeemError('');
    setReloadTick((n) => n + 1);
  };

  const toChildren = () => navigate({ to: '/children', replace: true });

  // Every way out of the invitation. On the student's own phone (mode=here)
  // the parent is signed out of THIS app only (the student's session is
  // untouched) and the hand-back screen shows, whether or not they linked;
  // the stored invite stays unless it is spent, so they can reopen the link
  // on their own phone. Elsewhere they go to My children.
  const leave = (outcome: ExitOutcome, typedName?: string) => {
    const plan = exitFlow(invite?.mode, outcome);
    if (plan.to === 'children') {
      toChildren();
      return;
    }
    // A child with no name when the page loaded is named by what the parent just typed.
    setLinkedName(childName ?? typedName ?? null);
    setHandbackVariant(plan.variant);
    setPhase('handback');
    logout();
  };

  const primary = (label: string, onClick: () => void, disabled = false) => (
    <button onClick={onClick} disabled={disabled} className="cta-primary">
      {label}
    </button>
  );
  const secondary = (label: string, onClick: () => void) => (
    <button onClick={onClick} className="cta-secondary">
      {label}
    </button>
  );
  const keptCallout = {
    icon: <IconClock size={20} aria-hidden />,
    title: t('keptTitle'),
    body: t('keptBody'),
  };

  const screen = (): ReactNode => {
    if (phase === 'handback') {
      return (
        <InviteShell
          hero={
            handbackVariant === 'linked'
              ? { icon: <IconCheck size={32} stroke={2} aria-hidden />, tone: 'teal' }
              : { icon: <IconClock size={32} stroke={1.75} aria-hidden />, tone: 'cream' }
          }
          title={
            handbackVariant === 'linked' ? t('allSetTitle') : t('handBackTitle', named(linkedName))
          }
          subtitle={
            handbackVariant === 'linked'
              ? t('allSetBody', named(linkedName))
              : handbackVariant === 'saved'
                ? t('handBackSavedBody', named(linkedName))
                : t('handBackEndedBody')
          }
          callout={
            handbackVariant === 'linked'
              ? {
                  icon: <IconClock size={20} aria-hidden />,
                  title: t('handBackTitle', named(linkedName)),
                  body: t('handBackBody', named(linkedName)),
                }
              : undefined
          }
        />
      );
    }

    if (!invite) {
      return (
        <InviteShell
          hero={{ icon: <IconAlertTriangle size={32} stroke={1.75} aria-hidden />, tone: 'cream' }}
          title={t('invalidTitle')}
          subtitle={t('invalidBody')}
          actions={secondary(t('openParent'), () =>
            navigate({ to: signedIn ? '/children' : '/start', replace: true })
          )}
        />
      );
    }

    if (!signedIn) {
      return (
        <InviteShell
          hero="mascot"
          title={t('entryTitle', named(null))}
          subtitle={t('entrySignedOutBody', named(null))}
          actions={
            <>
              {primary(t('createAccount'), () => navigate({ to: '/signup' }))}
              {secondary(t('haveAccount'), () => navigate({ to: '/login' }))}
            </>
          }
          footnote={t('savedNote')}
        />
      );
    }

    if (phase === 'later') {
      return (
        <InviteShell
          hero={{ icon: <IconClock size={32} stroke={1.75} aria-hidden />, tone: 'cream' }}
          title={t('laterTitle')}
          subtitle={t('laterBody', {
            ...named(childName),
            isUnder13: String(view?.kind === 'consent' && view.isUnder13),
          })}
          actions={
            <>
              {primary(t('giveConsentNow'), () => setPhase('view'))}
              {secondary(t('close'), () => leave('saved'))}
            </>
          }
        />
      );
    }

    // Signed in, invitation not yet confirmed (or still loading).
    if (!showView) {
      const hue = identityHue(accountName || 'P');
      return (
        <InviteShell
          hero="mascot"
          title={t('entryTitle', named(childName))}
          subtitle={t('entrySignedInBody', named(childName))}
          // The signed-in account is shown as a plain card, not a selected one:
          // Continue is the only primary action, Switch is a text link.
          body={
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3">
              <span
                className={`flex size-12 shrink-0 items-center justify-center rounded-pill text-lg font-extrabold ${hue.fill} ${hue.label}`}
                aria-hidden
              >
                {(accountName || 'P').charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-muted">{t('loggedInAs')}</p>
                <p className="truncate text-base font-extrabold text-content">{accountName}</p>
                {(phone || email) && (
                  <p className="truncate text-sm text-muted">{phone ? maskPhone(phone) : email}</p>
                )}
              </div>
              <button
                onClick={switchAccount}
                className="flex min-h-11 shrink-0 items-center px-2 text-sm font-bold text-primary"
              >
                {t('switchAccount')}
              </button>
            </div>
          }
          actions={primary(
            view === null ? t('checking') : t('continueCta'),
            () => setConfirmed(true),
            view === null
          )}
        />
      );
    }

    const v = view as InviteView; // non-null: showView implies a view
    switch (v.kind) {
      case 'consent':
        return (
          <InviteShell>
            <ChildConsent
              childName={childName}
              busy={redeeming}
              error={redeemError}
              onConfirm={handleConsent}
              onCancel={() => (invite?.mode === 'here' ? leave('saved') : setPhase('later'))}
            />
          </InviteShell>
        );
      case 'already_linked':
        return (
          <InviteShell
            hero={{ icon: <IconCheck size={32} stroke={2} aria-hidden />, tone: 'teal' }}
            title={t('alreadyLinkedTitle', named(childName))}
            subtitle={t('alreadyLinkedBody', named(childName))}
            actions={primary(t('goToChildren'), () => leave('linked'))}
          />
        );
      case 'blocked': {
        const warning = {
          icon: <IconAlertTriangle size={32} stroke={1.75} aria-hidden />,
          tone: 'peach',
        } as const;
        const changePlan = () =>
          navigate({ to: '/paywall', state: { trigger: `gate_${v.reason}` } });
        // The reference's two common blocks have their own copy; the rest
        // (trial seat, lapsed household, largest plan) use the gate copy.
        if (v.reason === 'seat_limit_reached' && free === null) {
          // Wait for access so paid copy never flashes.
          return <InviteShell />;
        }
        if (v.reason === 'seat_limit_reached' && free) {
          const upgradeDuo = () =>
            navigate({
              to: '/paywall',
              state: { trigger: `gate_${v.reason}`, seat: 'family_duo' },
            });
          return (
            <InviteShell
              hero={{
                icon: <IconAlertCircle size={32} stroke={1.75} aria-hidden />,
                tone: 'peach',
              }}
              title={t('freeFullTitle')}
              subtitle={t('freeFullBody', named(childName))}
              trailing={<span className="text-sm font-bold text-muted">{t('freePlanLabel')}</span>}
              callout={{
                icon: <IconClock size={20} aria-hidden />,
                title: t('freeNotLinked', named(childName)),
                body: t('freeCurrentStays'),
              }}
              actions={
                <>
                  <p className="text-center text-sm text-muted">{t('freeUpgradeNote')}</p>
                  {primary(t('freeUpgradeCta'), upgradeDuo)}
                  {secondary(ta('seePlans'), changePlan)}
                </>
              }
            />
          );
        }
        if (v.reason === 'seat_limit_reached' || v.reason === 'upgrade_required') {
          const full = v.reason === 'seat_limit_reached';
          return (
            <InviteShell
              hero={warning}
              title={full ? t('fullTitle') : t('syllabusTitle')}
              subtitle={
                full
                  ? t('fullBody', {
                      ...named(childName),
                      hasPlan: String(!!plan?.name),
                      planName: plan?.name ?? '',
                      seats: plan?.seats ?? 0,
                    })
                  : t('syllabusBody', named(childName))
              }
              callout={keptCallout}
              actions={
                <>
                  {primary(ta('changePlan'), changePlan)}
                  {secondary(ta('notNow'), () => leave('saved'))}
                </>
              }
            />
          );
        }
        const gate = gateCopy(v.reason, ta);
        // On the largest plan there is no bigger one to move to; the way out
        // is making room.
        const noBiggerPlan = v.reason === 'seat_limit_reached_max_tier';
        return (
          <InviteShell
            hero={warning}
            title={gate.title}
            subtitle={gate.body}
            callout={keptCallout}
            actions={
              <>
                {noBiggerPlan
                  ? primary(ta('manageChildren'), () => leave('saved'))
                  : primary(ta('changePlan'), changePlan)}
                {secondary(ta('notNow'), () => leave('saved'))}
              </>
            }
          />
        );
      }
      case 'taken':
        return (
          <InviteShell
            hero={{ icon: <IconLinkOff size={32} stroke={1.75} aria-hidden />, tone: 'cream' }}
            title={t('takenTitle')}
            subtitle={tc('errStudentAlreadyLinked')}
            actions={primary(t('goToChildren'), () => leave('ended'))}
          />
        );
      case 'expired':
        return (
          <InviteShell
            hero={{ icon: <IconClock size={32} stroke={1.75} aria-hidden />, tone: 'peach' }}
            title={t('expiredTitle')}
            subtitle={t('expiredBody')}
            actions={secondary(t('openParent'), () => leave('ended'))}
          />
        );
      case 'invalid':
        return (
          <InviteShell
            hero={{
              icon: <IconAlertTriangle size={32} stroke={1.75} aria-hidden />,
              tone: 'cream',
            }}
            title={t('invalidTitle')}
            subtitle={t('invalidBody')}
            actions={secondary(t('openParent'), () => leave('ended'))}
          />
        );
      default:
        return (
          <InviteShell
            hero={{
              icon: <IconAlertTriangle size={32} stroke={1.75} aria-hidden />,
              tone: 'cream',
            }}
            title={t('errorTitle')}
            subtitle={t('errorBody')}
            actions={
              <>
                {primary(t('tryAgain'), retry)}
                {secondary(ta('notNow'), () => leave('saved'))}
              </>
            }
          />
        );
    }
  };

  return (
    <AppShell variant="fullscreen" tone="surface" className="overflow-y-auto">
      {screen()}
    </AppShell>
  );
}
